import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import React, { FC, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogCloseIcon } from "../DialogCloseIcon/DialogCloseIcon";

export interface ConfirmDialogRenderProps {
  openConfirmation: () => void;
}
export interface ConfirmDialogWrapperProps {
  label: ReactNode;
  content: ReactNode;
  render: ({ openConfirmation }: ConfirmDialogRenderProps) => React.ReactNode;
  onConfirm?: () => void;
  onClose?: () => void;
}

/**
 * Component wraps any UI with a confirm dialog.
 * The render prop provides a function to open the dialog; handlers manage confirm/close.
 */
export const ConfirmDialogWrapper: FC<ConfirmDialogWrapperProps> = ({ render, onConfirm, onClose, label, content }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    setOpen(false);
    onConfirm?.();
  }, [onConfirm]);

  return (
    <>
      {render({
        openConfirmation: () => setOpen(true),
      })}
      <Dialog maxWidth="lg" onClose={handleClose} open={open}>
        <DialogTitle>
          {label}
          <DialogCloseIcon onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <Typography>{content}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("cancel")}</Button>
          <Button onClick={handleConfirm} variant="contained">
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
